import { formatNoteWhen, resolveNoteAuthorName } from '@/lib/utils/noteFormatting'
import type {
  CalculationAuditNoteInfo,
  CalculationAuditNotesMap,
} from './residentCalculationAuditNotes.types'

function parseSortTime(value: unknown): number {
  if (value === undefined || value === null || value === '') return 0
  if (typeof value === 'number') return value > 1e12 ? value : value * 1000
  const n = Number(value)
  if (!Number.isNaN(n) && String(value).trim().length <= 10) return n * 1000
  const d = Date.parse(String(value))
  return Number.isNaN(d) ? 0 : d
}

/**
 * Scans notes & messages for system audit lines on calculation-tab dates,
 * matching Angular's `processDefaultNotesandMessage`.
 */
export function parseCalculationAuditNotes(
  publicNotesAndMessages?: Array<Record<string, unknown>> | null,
): CalculationAuditNotesMap {
  if (!publicNotesAndMessages || !publicNotesAndMessages.length) return {}

  let rentalStatusDateNotes: CalculationAuditNoteInfo | undefined
  let newResidentLeaseStartDateNotes: CalculationAuditNoteInfo | undefined
  let moveindateNotes: CalculationAuditNoteInfo | undefined
  let adjustedNoticeNotes: CalculationAuditNoteInfo | undefined
  let adjustMaxMovinNotes: CalculationAuditNoteInfo | undefined
  let firstmonthNotes: CalculationAuditNoteInfo | undefined
  let firstmonthOverrideNotes: CalculationAuditNoteInfo | undefined

  for (const rawItem of publicNotesAndMessages) {
    if (!rawItem || typeof rawItem !== 'object') continue
    const text = String(rawItem.notes ?? rawItem.message ?? rawItem.content ?? '').trim()
    const isNoteType = rawItem.type === 'notes' || String(rawItem.email_send ?? '') === '0' || Boolean(rawItem.notes)

    if (isNoteType) {
      if (text === 'First Month Rent Not Paid has been updated' && !firstmonthNotes) {
        firstmonthNotes = {
          name: resolveNoteAuthorName(rawItem),
          formattedDate: formatNoteWhen(rawItem.created_at),
        }
      }

      if (
        text === 'First Month Rent Not Paid has been updated (by reimbursement override)' &&
        !firstmonthOverrideNotes
      ) {
        firstmonthOverrideNotes = {
          name: resolveNoteAuthorName(rawItem),
          formattedDate: formatNoteWhen(rawItem.created_at),
        }
      }

      if (text === 'Reimbursement End Date has been updated' && !adjustMaxMovinNotes) {
        adjustMaxMovinNotes = {
          name: resolveNoteAuthorName(rawItem),
          formattedDate: formatNoteWhen(rawItem.created_at),
        }
      } else if (text === 'Reimbursement End Date has been updated by system' && !adjustMaxMovinNotes) {
        adjustMaxMovinNotes = {
          name: 'System',
          formattedDate: formatNoteWhen(rawItem.created_at),
        }
      }

      if (text === 'Reimbursement Start Date has been updated' && !adjustedNoticeNotes) {
        adjustedNoticeNotes = {
          name: resolveNoteAuthorName(rawItem),
          formattedDate: formatNoteWhen(rawItem.created_at),
        }
      } else if (text === 'Reimbursement Start Date has been updated by system' && !adjustedNoticeNotes) {
        adjustedNoticeNotes = {
          name: 'System',
          formattedDate: formatNoteWhen(rawItem.created_at),
        }
      }

      if (text === 'Reimbursement Notification Date has been updated' && !rentalStatusDateNotes) {
        rentalStatusDateNotes = {
          name: resolveNoteAuthorName(rawItem),
          formattedDate: formatNoteWhen(rawItem.created_at),
        }
      }
    }
  }

  const sorted = [...publicNotesAndMessages].sort(
    (a, b) => parseSortTime(b.created_at) - parseSortTime(a.created_at),
  )

  for (const item of sorted) {
    const text = String(item.notes ?? item.message ?? item.content ?? '').trim()
    if (
      !moveindateNotes &&
      ['New resident move in date has been added', 'New Resident Move-In Date has been updated'].includes(text)
    ) {
      moveindateNotes = {
        name: resolveNoteAuthorName(item),
        formattedDate: formatNoteWhen(item.created_at),
      }
    }

    if (
      !newResidentLeaseStartDateNotes &&
      ['New resident lease start date has been added', 'New Resident Lease Start Date has been updated'].includes(text)
    ) {
      newResidentLeaseStartDateNotes = {
        name: resolveNoteAuthorName(item),
        formattedDate: formatNoteWhen(item.created_at),
      }
    }
  }

  return {
    rentalStatusDateNotes,
    newResidentLeaseStartDateNotes,
    moveindateNotes,
    adjustedNoticeNotes,
    adjustMaxMovinNotes,
    firstmonthNotes,
    firstmonthOverrideNotes,
  }
}
